/**
 * Editor Toolbox Component
 * Drag and drop components palette
 */
import React, { useState } from "react";
import { useEditor } from "@craftjs/core";
import { Box, Button as MuiButton, Typography, Paper } from "@mui/material";
import { DragIndicator as DragIndicatorIcon } from "@mui/icons-material";
import { componentCategories, componentDescriptions } from "../editorConstants";

export const EditorToolbox: React.FC = () => {
  const { connectors } = useEditor();
  const [activeCategory, setActiveCategory] = useState("Content");
  const [draggedComponent, setDraggedComponent] = useState<string | null>(null);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Category tabs - Enhanced */}
      <Box sx={{ p: 1, borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {componentCategories.map((cat) => (
            <MuiButton
              key={cat.name}
              size="small"
              variant={activeCategory === cat.name ? "contained" : "outlined"}
              onClick={() => setActiveCategory(cat.name)}
              sx={{
                fontSize: 12,
                textTransform: "none",
                py: 0.5,
                px: 1.5,
                minWidth: "auto",
                borderRadius: 2,
                ...(activeCategory !== cat.name && {
                  borderColor: "grey.300",
                  color: "text.secondary",
                  bgcolor: "white",
                }),
              }}
            >
              <span style={{ marginRight: 4 }}>{cat.icon}</span>
              {cat.name}
            </MuiButton>
          ))}
        </Box>
      </Box>

      {/* Drag hint */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: "primary.light",
          borderBottom: 1,
          borderColor: "primary.main",
          opacity: 0.9,
        }}
      >
        <Typography variant="caption" sx={{ color: "primary.dark", display: "flex", alignItems: "center", gap: 0.75 }}>
          <DragIndicatorIcon sx={{ fontSize: 14 }} />
          Drag components to the canvas
        </Typography>
      </Box>

      {/* Components grid - Enhanced */}
      <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1,
          }}
        >
          {componentCategories
            .find((c) => c.name === activeCategory)
            ?.components.map((comp) => (
              <Paper
                key={comp.name}
                ref={(ref) => {
                  if (ref) connectors.create(ref, comp.element);
                }}
                onDragStart={() => setDraggedComponent(comp.name)}
                onDragEnd={() => setDraggedComponent(null)}
                elevation={draggedComponent === comp.name ? 4 : 0}
                sx={{
                  position: "relative",
                  p: 1.5,
                  cursor: "grab",
                  transition: "all 0.2s",
                  border: 2,
                  borderColor: draggedComponent === comp.name ? "primary.main" : "grey.100",
                  bgcolor: draggedComponent === comp.name ? "primary.light" : "white",
                  transform: draggedComponent === comp.name ? "scale(1.05)" : "scale(1)",
                  borderRadius: 3,
                  "&:hover": {
                    borderColor: "primary.light",
                    boxShadow: 2,
                    background: "linear-gradient(135deg, #e3f2fd 0%, #fff 100%)",
                    "& .drag-handle": { opacity: 1 },
                    "& .comp-icon": { transform: "scale(1.1)" },
                    "& .comp-name": { color: "primary.main" },
                  },
                  "&:active": { cursor: "grabbing" },
                }}
              >
                {/* Drag handle indicator */}
                <Box
                  className="drag-handle"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    opacity: 0,
                    transition: "opacity 0.2s",
                    color: "grey.400",
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: 16 }} />
                </Box>

                {/* Icon */}
                <Typography className="comp-icon" sx={{ fontSize: 24, mb: 0.5, transition: "transform 0.2s" }}>
                  {comp.icon}
                </Typography>

                {/* Name */}
                <Typography
                  className="comp-name"
                  variant="caption"
                  fontWeight={500}
                  sx={{ transition: "color 0.2s", color: "text.primary" }}
                >
                  {comp.name}
                </Typography>

                {/* Description - shows on hover */}
                <Typography
                  variant="caption"
                  sx={{ fontSize: 10, color: "text.secondary", mt: 0.25, display: "block" }}
                >
                  {componentDescriptions[comp.name] || "Add to page"}
                </Typography>
              </Paper>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default EditorToolbox;
